import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Gift,
  ToggleLeft,
  ToggleRight,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { adminCampaignsApi } from '../../services/adminApi';

const campaignTypes = [
  { value: 'discount', label: 'İndirim' },
  { value: 'bundle', label: 'Paket' },
  { value: 'free', label: 'Ücretsiz' },
  { value: 'event', label: 'Etkinlik' }
];

const AdminCampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    business_id: '',
    business_name: '',
    image: '',
    discount: '',
    type: 'discount',
    valid_until: '',
    is_active: true
  });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const data = await adminCampaignsApi.getAll({
        search,
        status: statusFilter,
        page,
        limit: 20
      });
      setCampaigns(data.campaigns);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [search, statusFilter, page]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      if (editingCampaign) {
        await adminCampaignsApi.update(editingCampaign.id, formData);
      } else {
        await adminCampaignsApi.create(formData);
      }
      fetchCampaigns();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save campaign:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      title: campaign.title || '',
      description: campaign.description || '',
      business_id: campaign.businessId || '',
      business_name: campaign.businessName || '',
      image: campaign.image || '',
      discount: campaign.discount || '',
      type: campaign.type || 'discount',
      valid_until: campaign.validUntil || '',
      is_active: campaign.isActive !== false
    });
    setShowModal(true);
  };

  const handleToggleStatus = async (id) => {
    try {
      await adminCampaignsApi.toggleStatus(id);
      fetchCampaigns();
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kampanyayı silmek istediğinizden emin misiniz?')) return;
    try {
      await adminCampaignsApi.delete(id);
      fetchCampaigns();
    } catch (error) {
      console.error('Failed to delete campaign:', error);
    }
  };

  const resetForm = () => {
    setEditingCampaign(null);
    setFormData({
      title: '',
      description: '',
      business_id: '',
      business_name: '',
      image: '',
      discount: '',
      type: 'discount',
      valid_until: '',
      is_active: true
    });
  };

  const getTypeBadge = (type) => {
    const colors = {
      discount: 'bg-green-500/20 text-green-400',
      bundle: 'bg-blue-500/20 text-blue-400',
      free: 'bg-purple-500/20 text-purple-400',
      event: 'bg-orange-500/20 text-orange-400'
    };
    const label = campaignTypes.find(t => t.value === type)?.label || type;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type] || 'bg-slate-500/20 text-slate-400'}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Kampanyalar</h1>
          <p className="text-slate-400 mt-1">Toplam {total} kampanya</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Plus className="w-5 h-5 mr-2" />
          Yeni Kampanya
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            type="text"
            placeholder="Kampanya ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value="all">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="inactive">Pasif</option>
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          Kampanya bulunamadı
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((campaign) => (
            <div key={campaign.id} className={`bg-slate-800/50 border rounded-xl overflow-hidden transition-all ${campaign.isActive ? 'border-slate-700' : 'border-slate-700/50 opacity-60'}`}>
              <div className="relative h-40">
                <img src={campaign.image} alt={campaign.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  {getTypeBadge(campaign.type)}
                  {!campaign.isActive && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">Pasif</span>
                  )}
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-2xl font-bold text-white">{campaign.discount}</p>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-white font-semibold">{campaign.title}</h3>
                <p className="text-slate-400 text-sm mt-1">{campaign.businessName}</p>
                <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">
                  <Calendar className="w-4 h-4" />
                  {campaign.validUntil}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() => handleToggleStatus(campaign.id)}
                    className={`p-2 rounded-lg transition-colors ${campaign.isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'}`}
                  >
                    {campaign.isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(campaign)}
                      className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(campaign.id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
              <h3 className="text-lg font-semibold text-white">
                {editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Kampanya Başlığı</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">İşletme Adı</label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Görsel URL</label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">İndirim/Avantaj</label>
                  <Input
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                    placeholder="%20, 2+1, Ücretsiz"
                    required
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Tür</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  >
                    {campaignTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Geçerlilik Tarihi</label>
                <Input
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  placeholder="31 Ocak 2025"
                  required
                  className="bg-slate-900 border-slate-600 text-white"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-orange-500"
                />
                <label htmlFor="active" className="text-slate-300">Aktif kampanya</label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Kaydet'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCampaignsPage;
